import { SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * The distance in pixels from the top of the viewport used by the scroll spy
 * to decide which section is currently "in view".
 * @type {number}
 */
const SCROLL_SPY_OFFSET = 130;

/**
 * Class names used when building the sidebar navigation DOM.
 * Kept here rather than in @bs/constants because they are only used to
 * create elements, never to query them.
 * @type {object}
 */
const CLASS_NAMES = {
  ITEM: 'article-content__sidebar-item',
  LINK: 'article-content__sidebar-link',
  SUBLIST: 'article-content__sidebar-sublist',
  SUBITEM: 'article-content__sidebar-subitem',
  SUBLINK: 'article-content__sidebar-sublink'
};

/**
 * Returns the id of a heading, generating a unique slug from its text
 * content when it does not have one so it can be used as an anchor target.
 * @param {HTMLElement} heading - The heading element to identify.
 * @param {number} index - The document order index used as a fallback slug.
 * @returns {string} The id assigned to the heading.
 */
const ensureHeadingId = (heading, index) => {
  if (heading.id) return heading.id;

  const slug = heading.textContent
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  let id = slug || `article-section-${index + 1}`;
  let suffix = 2;

  while (document.getElementById(id)) {
    id = `${slug || 'article-section'}-${suffix}`;
    suffix += 1;
  }

  heading.setAttribute('id', id);

  return id;
};

/**
 * A class for the Article Sidebar module.
 * Builds an anchor navigation from the H2 (parent) and H3 (child) headings
 * rendered by the article-content flex layouts, smooth-scrolls to a section
 * when a link is clicked and highlights the section currently in view while
 * scrolling. On mobile the navigation collapses into a toggle button that is
 * pinned to the bottom of the viewport by the styles.
 */
export default class ArticleSidebar extends MessageBus {
  /**
   * Constructor function of the ArticleSidebar class. Must receive one
   * argument, a DOM node representing the article content block element.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();

    /**
     * DOM node that is passed into the constructor
     * @property {HTMLElement} element DOM node passed into the constructor
     */
    this.element = element;

    /**
     * The class applied to the active item and links.
     * @property {string} activeClass - CSS class toggled on active items.
     */
    this.activeClass = 'is-active';

    /**
     * The class applied to the nav when the mobile menu is expanded.
     * @property {string} openClass - CSS class toggled on the nav element.
     */
    this.openClass = 'is-open';

    /**
     * Flat list of the sections in document order used by the scroll spy.
     * @property {Array<object>} sections - Entries holding the heading
     * element, its top level item and, for H3 headings, the child link.
     */
    this.sections = [];

    /**
     * Top level sidebar items, one per H2 heading.
     * @property {Array<object>} items - Entries holding the parent list
     * item, its link and its lazily created sublist.
     */
    this.items = [];

    /**
     * The index of the currently active section.
     * @property {number} activeIndex - Index within `this.sections`; -1
     * before the first scroll spy update runs.
     */
    this.activeIndex = -1;

    /**
     * Guard used to throttle scroll handling with requestAnimationFrame.
     * @property {boolean} ticking - True while an animation frame is pending.
     */
    this.ticking = false;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners.
   * @returns {ArticleSidebar} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them.
   * @returns {ArticleSidebar} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.sidebar = this.element.querySelector(SELECTORS.ARTICLE_SIDEBAR);

    this.contentBlock = this.element.querySelector(
      SELECTORS.ARTICLE_SIDEBAR_CONTENT
    );

    this.nav = this.sidebar
      ? this.sidebar.querySelector(SELECTORS.ARTICLE_SIDEBAR_NAV)
      : null;

    this.toggle = this.sidebar
      ? this.sidebar.querySelector(SELECTORS.ARTICLE_SIDEBAR_TOGGLE)
      : null;

    this.toggleLabel = this.sidebar
      ? this.sidebar.querySelector(SELECTORS.ARTICLE_SIDEBAR_TOGGLE_LABEL)
      : null;

    this.list = this.sidebar
      ? this.sidebar.querySelector(SELECTORS.ARTICLE_SIDEBAR_LIST)
      : null;

    this.headings = this.contentBlock
      ? Array.from(
          this.contentBlock.querySelectorAll(SELECTORS.ARTICLE_SIDEBAR_HEADINGS)
        )
      : [];

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {ArticleSidebar} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleLinkClick = this.handleLinkClick.bind(this);
    this.handleScroll = this.handleScroll.bind(this);

    return this;
  }

  /**
   * Create event listeners to enable interaction with the view.
   * Bails out when the sidebar was not rendered (the `sidebar` field is
   * false) and hides it entirely when the content contains no headings.
   * @returns {ArticleSidebar} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    // The sidebar is rendered conditionally — bail when it is not present
    if (!this.sidebar || !this.list || !this.contentBlock) return this;

    // Nothing to link to — hide the sidebar entirely and bail
    if (!this.headings.length) {
      this.sidebar.hidden = true;
      return this;
    }

    this.buildNav();

    if (this.toggle) {
      this.toggle.addEventListener('click', this.handleToggleClick);
    }

    this.list.addEventListener('click', this.handleLinkClick);

    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // Highlight the correct section on load (e.g. when landing on a hash)
    this.updateActiveSection();

    return this;
  }

  // ---------------------------------------------------------------------------
  // Navigation building
  // ---------------------------------------------------------------------------

  /**
   * Builds the sidebar list from the headings found in the content block.
   * H2 headings become parent items and H3 headings become children of the
   * last seen H2. An H3 that appears before any H2 is promoted to a parent
   * item so it is never silently dropped.
   * @returns {ArticleSidebar} A reference to the current instance of the class
   * @chainable
   */
  buildNav() {
    const fragment = document.createDocumentFragment();
    let currentItem = null;

    this.headings.forEach((heading, index) => {
      const id = ensureHeadingId(heading, index);
      const isParent = heading.tagName === 'H2' || !currentItem;

      if (isParent) {
        const item = document.createElement('li');
        item.className = CLASS_NAMES.ITEM;

        const link = document.createElement('a');
        link.className = CLASS_NAMES.LINK;
        link.href = `#${id}`;
        link.textContent = heading.textContent.trim();

        item.appendChild(link);
        fragment.appendChild(item);

        currentItem = { element: item, link, sublist: null };

        this.items.push(currentItem);
        this.sections.push({ heading, item: currentItem, childLink: null });
      } else {
        if (!currentItem.sublist) {
          currentItem.sublist = document.createElement('ul');
          currentItem.sublist.className = CLASS_NAMES.SUBLIST;
          currentItem.element.appendChild(currentItem.sublist);
        }

        const subitem = document.createElement('li');
        subitem.className = CLASS_NAMES.SUBITEM;

        const sublink = document.createElement('a');
        sublink.className = CLASS_NAMES.SUBLINK;
        sublink.href = `#${id}`;
        sublink.textContent = heading.textContent.trim();

        subitem.appendChild(sublink);
        currentItem.sublist.appendChild(subitem);

        this.sections.push({ heading, item: currentItem, childLink: sublink });
      }
    });

    this.list.appendChild(fragment);

    return this;
  }

  // ---------------------------------------------------------------------------
  // Scroll spy
  // ---------------------------------------------------------------------------

  /**
   * Handles the window scroll event, throttled to one update per frame.
   */
  handleScroll() {
    if (this.ticking) return;

    this.ticking = true;

    window.requestAnimationFrame(() => {
      this.ticking = false;
      this.updateActiveSection();
    });
  }

  /**
   * Finds the last heading that has scrolled past the scroll spy offset and
   * marks its section as active. Falls back to the first section when no
   * heading has been reached yet.
   * @returns {ArticleSidebar} A reference to the current instance of the class
   * @chainable
   */
  updateActiveSection() {
    let activeIndex = 0;

    this.sections.forEach((section, index) => {
      if (section.heading.getBoundingClientRect().top <= SCROLL_SPY_OFFSET) {
        activeIndex = index;
      }
    });

    this.setActiveSection(activeIndex);

    return this;
  }

  /**
   * Applies the active state for the section at the given index: highlights
   * the parent item (which also reveals its children on desktop), highlights
   * the child link when the section is an H3 and mirrors the parent label on
   * the mobile toggle.
   * @param {number} index - The index of the section within `this.sections`.
   * @returns {ArticleSidebar} A reference to the current instance of the class
   * @chainable
   */
  setActiveSection(index) {
    if (index === this.activeIndex) return this;

    this.activeIndex = index;

    const activeSection = this.sections[index];

    this.items.forEach((item) => {
      const isActive = item === activeSection.item;

      item.element.classList.toggle(this.activeClass, isActive);

      if (isActive) {
        item.link.setAttribute('aria-current', 'true');
      } else {
        item.link.removeAttribute('aria-current');
      }
    });

    this.sections.forEach((section) => {
      if (!section.childLink) return;

      const isActive = section === activeSection;

      section.childLink.classList.toggle(this.activeClass, isActive);

      if (isActive) {
        section.childLink.setAttribute('aria-current', 'true');
      } else {
        section.childLink.removeAttribute('aria-current');
      }
    });

    // Mirror the active parent label on the collapsed mobile toggle
    if (this.toggleLabel) {
      this.toggleLabel.textContent = activeSection.item.link.textContent;
    }

    return this;
  }

  // ---------------------------------------------------------------------------
  // Interaction
  // ---------------------------------------------------------------------------

  /**
   * Handles a click on the mobile toggle button by expanding or collapsing
   * the navigation list and keeping `aria-expanded` in sync.
   */
  handleToggleClick() {
    const isOpen = this.nav.classList.toggle(this.openClass);

    this.toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  /**
   * Handles a delegated click on a sidebar anchor link by smooth scrolling
   * to the target heading, updating the URL hash and closing the mobile menu.
   * @param {Event} event - The click event fired within the list.
   */
  handleLinkClick(event) {
    const link = event.target.closest(SELECTORS.ARTICLE_SIDEBAR_ANCHOR);

    if (!link || !this.list.contains(link)) return;

    const target = document.getElementById(
      decodeURIComponent(link.hash.slice(1))
    );

    if (!target) return;

    event.preventDefault();

    target.scrollIntoView({ behavior: 'smooth', block: 'start' });

    window.history.pushState(null, '', link.hash);

    this.closeMenu();
  }

  /**
   * Collapses the mobile navigation list. Harmless on desktop where the
   * toggle is hidden and the list is always visible.
   * @returns {ArticleSidebar} A reference to the current instance of the class
   * @chainable
   */
  closeMenu() {
    if (!this.nav) return this;

    this.nav.classList.remove(this.openClass);

    if (this.toggle) {
      this.toggle.setAttribute('aria-expanded', 'false');
    }

    return this;
  }
}
